import {CircularProgress} from "@mui/material";
import React, {useContext, useEffect, useState} from "react";
import {defer, useParams} from "react-router-dom";
import {getCustomer} from "../../api/customerClient";
import {listCustomers} from "../../api/customerClient";
import {getCustomerTemplatesForAdmins, getCustomerTemplatesForUsers} from "../../api/templateClient";
import {TemplateWithoutBody} from "../../model/Template";
import {Customer} from "../../model/Customer";
import {AdminTemplateOverview} from "../../components/Template/AdminTemplateOverview";
import {UserTemplateOverview} from "../../components/Template/UserTemplateOverview";
import {UserContext} from "../../App";
import {OrganizationLevel} from "../../model/SelfUser";

const TemplateManagerPage: React.FC = (props) => {
    const {user} = useContext(UserContext);
    const isAdmin = user?.organizationLevel === OrganizationLevel.SYSTEM;
    const {customerId} = useParams();

    const [templates, setTemplates] = useState<TemplateWithoutBody[] | null>(null);
    const [customers, setCustomers] = useState<Customer[] | null>(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        let isMounted = true;
        const load = async () => {
            try {
                const [templatesData, customersData] = await Promise.all([
                    isAdmin
                        ? getCustomerTemplatesForAdmins(customerId!)
                        : getCustomerTemplatesForUsers(customerId!),
                    isAdmin
                        ? (async () => {
                            const response = await listCustomers("1");
                            if (response.ok) {
                                return await response.json();
                            }
                            return [];
                        })()
                        : Promise.resolve([])
                ]);
                
                if (isMounted) {
                    setTemplates(templatesData);
                    setCustomers(customersData);
                    setLoading(false);
                }
            } catch (e) {
                if (isMounted) {
                    setTemplates([]);
                    setCustomers([]);
                    setLoading(false);
                }
            }
        };
        load();
        return () => { isMounted = false; }
    }, [customerId, isAdmin]);

    if (loading || !templates) {
        return <CircularProgress/>;
    }

    return isAdmin
        ? <AdminTemplateOverview templates={templates} customers={customers || []}/>
        : <UserTemplateOverview templates={templates}/>;
}

export default TemplateManagerPage;

export async function customerLoader({request, params}: { request: Request, params: { customerId: string } }) {
    const id = params.customerId;

    return defer({
        customer: getCustomer(id)
    })
}

// Backwards-compatible helper used by campaign pages
export async function loadCustomerTemplates(id: string) {
    return getCustomerTemplatesForAdmins(id);
}